import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Select, Textarea } from '@/components/ui'
import { CreditCard, Copy, Check, Trash2, Filter } from 'lucide-react'
import { toast } from 'sonner'

export function CreditCardGenerator() {
  const [bin, setBin] = useState('')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')
  const [cvv, setCvv] = useState('')
  const [quantity, setQuantity] = useState(10)
  const [result, setResult] = useState('')
  const [copied, setCopied] = useState(false)
  const [useCustomMonth, setUseCustomMonth] = useState(true)
  const [useCustomCvv, setUseCustomCvv] = useState(true)
  const [cardLength, setCardLength] = useState(16)

  // Reference: rand(min, max)
  // Reference: rand — 1:1 port (rand(0,9) returns 0-8, rand(1,12) returns 1-12)
  const rand = (min: number, max: number): number => {
    return min === 0
      ? Math.floor(Math.random() * max + 0)
      : Math.floor(Math.random() * (max - min + 1)) + min
  }

  // Reference: strrev
  const strrev = (s: string): string => s.split('').reverse().join('')

  // Reference: pad
  const pad = (val: number, size: number): string => {
    const s = '0' + val
    return s.substr(s.length - size)
  }

  // Reference: addPlaceholder — called on blur of BIN input
  const addPlaceholder = (value: string) => {
    if (value.length < 6) return value

    let b = value.replace(/\s+/g, '')
    const len = /^3/.test(b) ? 15 : 16
    setCardLength(len)
    b = b.replace(/X/g, 'x')
    b = b.replace(/[^0-9x]/g, '')

    let placeholder = ''
    for (let i = 0; i < len - b.length; i++) {
      placeholder += 'x'
    }
    b += placeholder

    return b
  }

  // Reference: makeCC — exact 1:1 port from reference_gen.js
const makeCC = (inputBin: string, len: number): string => {
  let e = ''

  inputBin.split('').forEach((ch) => {
    if (e.length < len - 1) {
      e += (ch.toLowerCase() === 'x') ? String(rand(0, 9)) : ch
    }
  })

  const rev = strrev(e) // should return a string
  let sum = 0

  for (let r = 0; r < len - 1; r += 2) {
    let dbl = 2 * Number(rev[r])
    if (dbl > 9) dbl -= 9
    sum += dbl

    if (r !== len - 2) sum += parseInt(rev[r + 1], 10)
  }

  const check = (10 * (Math.floor(sum / 10) + 1) - sum) % 10
  return e + (Number.isNaN(check) ? '' : String(check))
}

  const generateCards = () => {
    if (!bin || bin.length < 6) {
      toast.error('Please enter a valid BIN (minimum 6 digits)')
      return
    }

    const len = /^3/.test(bin) ? 15 : 16
    const cards: string[] = []

    for (let i = 0; i < quantity; i++) {
      const cardNumber = makeCC(bin, len)

      let line = cardNumber

      // Reference: generateDate
      if (useCustomMonth || month || year) {
        let expMonth = ''
        let expYear = ''

        if (!useCustomMonth || !month) {
          expMonth = pad(rand(1, 12), 2)
        } else {
          expMonth = month
        }

        if (!year) {
          expYear = String(parseInt(String(new Date().getFullYear())) + parseInt(String(rand(2, 5))))
        } else {
          expYear = year
        }

        line += '|' + expMonth + '|' + expYear
      }

      // Reference: generateCCV2
      if (useCustomCvv || cvv) {
        if (cvv) {
          line += '|' + cvv
        } else {
          line += '|' + rand(0, 9).toString() + rand(0, 9).toString() + rand(0, 9).toString() + (len === 15 ? rand(0, 9).toString() : '')
        }
      }

      cards.push(line)
    }

    setResult(cards.join('\n'))
    toast.success(`Generated ${cards.length} cards`)
  }

  const removeDuplicatesFromResult = () => {
    if (!result) return
    const lines = result.split('\n').filter(Boolean)
    const seen = new Set<string>()
    const unique: string[] = []

    for (const line of lines) {
      const cardNum = line.split('|')[0]
      if (!seen.has(cardNum)) {
        seen.add(cardNum)
        unique.push(line)
      }
    }

    const removed = lines.length - unique.length
    setResult(unique.join('\n'))
    if (removed > 0) {
      toast.success(`Removed ${removed} duplicate${removed > 1 ? 's' : ''}`)
    } else {
      toast.info('No duplicates found')
    }
  }

  const copyToClipboard = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearResult = () => {
    setResult('')
    toast.success('Cleared')
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 21 }, (_, i) => currentYear + i)
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <CreditCard className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Credit Card Generator</h1>
            <p className="text-muted-foreground">Generate valid test credit card numbers from BIN</p>
          </div>
        </div>
      </div>

      <Card className="glass border-yellow-500/50">
        <CardContent className="p-4">
          <p className="text-sm text-yellow-500">
            <strong>Disclaimer:</strong> These card numbers are for testing purposes only.
            They pass Luhn validation but cannot be used for real transactions.
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <Card className="glass">
          <CardHeader>
            <CardTitle>Generator</CardTitle>
            <CardDescription>Enter BIN and configure options</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* BIN Input */}
            <div className="space-y-2">
              <Label htmlFor="bin">BIN</Label>
              <Input
                id="bin"
                type="text"
                value={bin}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9xX]/g, '')
                  setBin(val)
                  // Update card length live
                  if (val.length >= 1) {
                    setCardLength(/^3/.test(val) ? 15 : 16)
                  }
                }}
                onBlur={(e) => {
                  const padded = addPlaceholder(e.target.value)
                  if (padded) setBin(padded)
                }}
                placeholder="Enter BIN (e.g., 549627003485)"
                className="font-mono"
                autoFocus
              />
              {bin.length >= 6 && (
                <p className="text-xs text-muted-foreground">
                  Card length: {cardLength} &middot; {Math.max(0, bin.split('').filter(c => c.toLowerCase() === 'x').length - 1)} random + 1 check digit
                </p>
              )}
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>Date</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="customMonth"
                  checked={useCustomMonth}
                  onChange={(e) => setUseCustomMonth(e.target.checked)}
                  className="h-4 w-4 rounded border-border shrink-0"
                />
                <Select
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-24"
                  disabled={!useCustomMonth}
                >
                  <option value="">Random</option>
                  {months.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </Select>
                <span className="text-muted-foreground">/</span>
                <Select
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-28"
                >
                  <option value="">Random</option>
                  {years.map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </Select>
              </div>
            </div>

            {/* CVV and Quantity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cvv">CVV</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="customCvv"
                    checked={useCustomCvv}
                    onChange={(e) => setUseCustomCvv(e.target.checked)}
                    className="h-4 w-4 rounded border-border"
                  />
                  <Input
                    id="cvv"
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Random"
                    className="font-mono flex-1"
                    disabled={!useCustomCvv}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.min(70000, Math.max(1, Number(e.target.value))))}
                  min="1"
                  max="70000"
                />
              </div>
            </div>

            {/* Generate Button */}
            <Button onClick={generateCards} className="w-full">
              Generate Cards
            </Button>
          </CardContent>
        </Card>

        {/* Result */}
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Result</CardTitle>
                <CardDescription>Generated card numbers</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeDuplicatesFromResult}
                  disabled={!result}
                  title="Remove duplicates from result"
                >
                  <Filter className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  disabled={!result}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearResult}
                  disabled={!result}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={result}
              onChange={(e) => setResult(e.target.value)}
              placeholder="Generated cards will appear here...&#10;&#10;Format: CardNumber|Month|Year|CVV"
              className="font-mono text-sm min-h-[300px] resize-none"
              readOnly
            />
            {result && (
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-muted-foreground">
                  {result.split('\n').filter(Boolean).length} cards generated
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeDuplicatesFromResult}
                  className="text-xs h-7 text-muted-foreground hover:text-foreground"
                >
                  <Filter className="h-3 w-3 mr-1" />
                  Remove Duplicates
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick BINs */}
      <Card className="glass">
        <CardHeader>
          <CardTitle>Quick BINs</CardTitle>
          <CardDescription>Click to use common test BINs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Visa', bin: '414720' },
              { label: 'Visa', bin: '456789' },
              { label: 'Mastercard', bin: '512345' },
              { label: 'Mastercard', bin: '555555' },
              { label: 'Amex', bin: '371449' },
              { label: 'Amex', bin: '378282' },
              { label: 'Discover', bin: '601100' },
              { label: 'JCB', bin: '352800' },
            ].map((item) => (
              <Button
                key={item.bin}
                variant="outline"
                size="sm"
                onClick={() => {
                  const padded = addPlaceholder(item.bin)
                  setBin(padded || item.bin)
                }}
                className="font-mono"
              >
                {item.label}: {item.bin}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
